import type { ComponentType } from "react";
import {
  LayoutGrid,
  Users,
  Briefcase,
  CreditCard,
  PiggyBank,
  Building2,
  ShoppingBag,
  Factory,
  ShieldCheck,
  FileText,
  BarChart3,
  ClipboardList,
  Calculator,
  DollarSign,
  Boxes,
} from "lucide-react";
import { cn } from "@/lib/utils";

type ModuleAvatarSize = "sm" | "md" | "lg" | "xl";

const iconMap: Record<string, ComponentType<{ className?: string }>> = {
  "layout-grid": LayoutGrid,
  users: Users,
  briefcase: Briefcase,
  "credit-card": CreditCard,
  "piggy-bank": PiggyBank,
  building: Building2,
  "shopping-bag": ShoppingBag,
  factory: Factory,
  "shield-check": ShieldCheck,
  file: FileText,
  chart: BarChart3,
  list: ClipboardList,
  calculator: Calculator,
  "currency-dollar": DollarSign,
  "dollar-sign": DollarSign,
  boxes: Boxes,
};

const sizeClasses: Record<ModuleAvatarSize, { container: string; icon: string }> = {
  sm: { container: "h-6 w-6", icon: "h-3.5 w-3.5 text-current" },
  md: { container: "h-7 w-7", icon: "h-4 w-4 text-current" },
  lg: { container: "h-11 w-11", icon: "h-6 w-6 text-current" },
  xl: { container: "h-24 w-24", icon: "h-10 w-10 text-current" },
};

const colorClasses: Record<string, string> = {
  emerald: "bg-emerald-500 text-white",
  sky: "bg-sky-500 text-white",
  amber: "bg-amber-500 text-amber-950",
  rose: "bg-rose-500 text-white",
  green: "bg-emerald-500 text-white",
  slate: "bg-slate-600 text-white",
};

interface ModuleAvatarProps {
  icon?: string;
  color?: string;
  size?: ModuleAvatarSize;
  className?: string;
}

export default function ModuleAvatar({ icon, color, size = "md", className }: ModuleAvatarProps) {
  const Icon = icon ? iconMap[icon] : undefined;
  const classes = sizeClasses[size];
  const colorClass = color ? colorClasses[color] : colorClasses.slate;

  return (
    <span
      className={cn(
        "flex items-center justify-center rounded-md text-xs font-bold",
        classes.container,
        colorClass,
        className
      )}
    >
      {Icon ? <Icon className={classes.icon} /> : null}
    </span>
  );
}
